import { useQuery } from "@tanstack/react-query";
import type { Manual, ManualTrash } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { manualApi } from "../../api/client.js";

/**
 * A facility's manuals (`GET /facilities/:id/manuals`, server-sorted by category then name) for the manuals view —
 * ACTIVE by default, or the ARCHIVED set when `archived` is true (the Archived filter). `archived` is in the query key
 * so the two lists cache separately. Keyed by facility id so switching the selected facility refetches; gated until a
 * facility is selected so merely browsing the tree fires no request. Errors surface as a thrown error (error state).
 */
export function useFacilityManualTrash(session: AuthSession | null, facilityId: number | null) {
  return useQuery({
    queryKey: ["facility-manual-trash", session?.realm, session?.sub, facilityId],
    enabled: session !== null && facilityId !== null,
    queryFn: async (): Promise<ManualTrash> => {
      const res = await manualApi.listFacilityManualTrash({ params: { id: facilityId! } });
      if (res.status !== 200) throw new Error(`GET /facilities/${facilityId!}/manuals/trash returned ${res.status}`);
      return res.body;
    },
  });
}

export function useFacilityManuals(session: AuthSession | null, facilityId: number | null, archived = false) {
  return useQuery({
    queryKey: ["facility-manuals", session?.realm, session?.sub, facilityId, archived],
    enabled: session !== null && facilityId !== null,
    queryFn: async (): Promise<Manual[]> => {
      const res = await manualApi.listFacilityManuals({
        params: { id: facilityId! },
        query: { archived: archived ? "true" : "false" },
      });
      if (res.status !== 200) throw new Error(`GET /facilities/${facilityId!}/manuals?archived=${archived} returned ${res.status}`);
      return res.body.manuals;
    },
  });
}
